import { ProgramCategory } from '@monorepo/types';
import { Theme } from '@mui/material';

export interface NodeState {
  active: boolean;
  completed: boolean;
  category: ProgramCategory;
  isNextAfterActive: boolean;
  hasError?: boolean;
}

export const getNodeSvgColor = (
  theme: Theme,
  ownerState: NodeState,
): string => {
  // Error state: use white
  if (ownerState.hasError && ownerState.active) {
    return theme.palette.common.white;
  }

  // Special case: funded steps with primary background always use white icon
  if (
    ownerState.category === ProgramCategory.Funded &&
    (ownerState.completed || ownerState.active)
  ) {
    return theme.palette.text.opposite;
  }
  // Current step icon always white (check active first to ensure it takes precedence)
  if (ownerState.active) {
    return theme.palette.common.white;
  }

  if (ownerState.completed) return theme.palette.common.white;
  if (ownerState.isNextAfterActive) {
    return theme.palette.info.main; // Next step uses info color
  }
  return theme.palette.text.third; // Not active nodes
};

export const getNodeBackgroundColor = (
  theme: Theme,
  ownerState: NodeState,
): string => {
  // Error state overrides all other states
  if (ownerState.hasError && ownerState.active) {
    return theme.palette.error.main;
  }
  // Special case: funded steps always use primary when user passes them
  if (
    ownerState.category === ProgramCategory.Funded &&
    (ownerState.completed || ownerState.active)
  ) {
    return theme.palette.primary.main;
  }
  if (ownerState.completed) return theme.palette.secondaryInvert.main;
  if (ownerState.active) {
    // All other active nodes get secondaryInvert background
    return theme.palette.secondaryInvert.main;
  }
  if (ownerState.isNextAfterActive) {
    // Next node after active gets contrast background
    return theme.palette.background.contrast;
  }
  return theme.palette.text.fifth; // Not active nodes
};

export const getNodeBorder = (theme: Theme, ownerState: NodeState): string => {
  // Error state: white border
  if (ownerState.hasError && ownerState.active) {
    return `1px solid ${theme.palette.common.white}`;
  }
  if (ownerState.active) return 'none'; // All active nodes
  if (ownerState.isNextAfterActive) {
    return `1px solid ${theme.palette.info.main}`; // Next step uses info border
  }
  return 'none'; // Not active nodes
};

export const getNodeTextColor = (
  theme: Theme,
  ownerState: NodeState,
): string => {
  // Error state overrides all other states
  if (ownerState.hasError && ownerState.active) {
    return theme.palette.text.opposite;
  }
  // Special case: funded steps always use primary when user passes them
  if (
    ownerState.category === ProgramCategory.Funded &&
    (ownerState.completed || ownerState.active)
  ) {
    return theme.palette.text.opposite;
  }
  if (ownerState.completed) return theme.palette.text.opposite;
  if (ownerState.active) return theme.palette.text.opposite; // All active nodes
  if (ownerState.isNextAfterActive) {
    // Special case: funded steps get primary color
    if (ownerState.category === ProgramCategory.Funded) {
      return theme.palette.primary.main;
    }
    return theme.palette.secondaryInvert.main; // Next node after active
  }
  return theme.palette.text.third; // Not active nodes
};
